const { DEFAULT_VERSION } = require("../constants")

/**
 * gets property value from the specified path passed
 * @param {object} from - object which needs to be parsed (e.g. window)
 * @param {string} path - path of the property value (e.g. window.path1.path2)
 */
const getPropertyPath = (from, path) => {
    if (!path) return ""
    return path
        .replace(/\[([^\[\]]*)\]/g, ".$1.") // eslint-disable-line
        .split(".")
        .filter((t) => t !== "")
        .reduce((prev, cur) => prev && prev[cur], from)
}

// logger object (info, error)
const logger = {
    info: (log) => {
        if (typeof window !== "undefined") console.info(log) // temp: disabling server logs
    },
    error: (err) => {
        if (typeof window !== "undefined") console.error(err) // temp: disabling server logs
    },
}

/**
 * try-catch handler for callbacks
 * @param {function} cb - callback
 */
function exceptionHandler(cb) {
    return function (...args) {
        const context = this
        try {
            cb.apply(context, args)
        } catch (error) {
            logger.error(error)
        }
    }
}

/**
 * sanitizes platform version number
 * @param {string} version - platform version (e.g. "1.7.1", "1.1.2-rc")
 * @returns {string|boolean}
 */
const sanitizeVersion = (version) => {
    if (version === DEFAULT_VERSION) return version
    // remove anything after - 1.1.2-rc
    function cropDash(s) {
        const idx = s.indexOf("-")
        if (idx !== -1) {
            s = s.substring(0, idx)
        }
        return s
    }
    function isValidPart(x) {
        return /^\d+$/.test(x)
    }
    const v = cropDash(version)
    let parts = v.split(".")
    if (!parts.every(isValidPart)) {
        return false
    }
    return v
}

/**
 * used for injecting native callbacks in raw HTML docs and scripts
 * @param {string} interfaceName - name of the interface called
 * @param {string} methodName - name of the method within the interface called
 * @param {object|string|boolean} [args] - arguments passed inside the callback
 * @param {string} [nonce] - script nonce attribute
 * @returns {string}
 */
const injectCallback = (interfaceName = "", methodName = "", args = {}, nonce = "") => {
    const payload = JSON.stringify({ type: methodName, data: args })
    return `
      <script nonce=${nonce}>
        const androidPlatformPrefix = window.AppCallbacks;
        const iosPlatformPrefix = window.webkit && window.webkit.messageHandlers;
        if (androidPlatformPrefix &&
            typeof androidPlatformPrefix.${interfaceName} === "function") {
            try {
                androidPlatformPrefix.${interfaceName}(${JSON.stringify(payload)});
            } catch (error) {
                console.log("WebView Error!!", error.message);
            }
        }
        if (iosPlatformPrefix && iosPlatformPrefix.${interfaceName} &&
            typeof iosPlatformPrefix.${interfaceName}.postMessage === "function") {
            try {
                iosPlatformPrefix.${interfaceName}.postMessage(${JSON.stringify(payload)});
            } catch (error) {
                console.log("WebView Error!!", error.message);
            }
        }
      </script>
  `
}

/**
 * used for injecting native callbacks content in raw HTML docs and scripts
 * @param {string} interfaceName - name of the interface called
 * @param {string} methodName - name of the method within the interface called
 * @param {object|string|boolean} [args] - arguments passed inside the callback
 * @returns {string}
 */
const injectCallbackContent = (interfaceName = "", methodName = "", args = {}) => {
    const payload = JSON.stringify({ type: methodName, data: args })
    return `
        const androidPlatformPrefix = window.AppCallbacks;
        const iosPlatformPrefix = window.webkit && window.webkit.messageHandlers;
        if (androidPlatformPrefix &&
            typeof androidPlatformPrefix.${interfaceName} === "function") {
            try {
                androidPlatformPrefix.${interfaceName}(${JSON.stringify(payload)});
            } catch (error) {
                console.log("WebView Error!!", error.message);
            }
        }
        if (iosPlatformPrefix && iosPlatformPrefix.${interfaceName} &&
            typeof iosPlatformPrefix.${interfaceName}.postMessage === "function") {
            try {
                iosPlatformPrefix.${interfaceName}.postMessage(${JSON.stringify(payload)});
            } catch (error) {
                console.log("WebView Error!!", error.message);
            }
        }
  `
}

/**
 * parser for the callback name, used for native resolve/reject callbacks consumption
 * @param {string} callbackName - name of the callback, format - "interfaceName.methodName"
 * @returns {object|null}
 */
const callbackParser = (callbackName = "") => {
    const definitionArr = callbackName.split(".")
    if (definitionArr.length === 2) {
        return {
            interfaceName: definitionArr[0],
            methodName: definitionArr[1],
        }
    }
    return null
}

module.exports = {
    logger,
    callbackParser,
    injectCallback,
    getPropertyPath,
    sanitizeVersion,
    exceptionHandler,
    injectCallbackContent,
}
